/*
 *  -----------------------------------------------------------------------
 *    <copyright file="delete-organisation.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { useTranslation, useBoolean } from 'hooks';
import { DELETE_ORGANISATION } from 'queries/org-admin';
import { SaveButton } from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from 'views/components/core/dialog';
import TextField from 'views/components/core/text-field';

import './styles.scss';

interface IDeleteOrganisation {
    open: boolean;
    onClose: Function;
    organisations: any[];
    orgName: string;
    orgKey: string;
}

const DeleteOrganisation = ({ open, onClose, organisations, orgName, orgKey }: IDeleteOrganisation) => {
    const { t } = useTranslation();
    const [deleteOrg, { data, error, loading }] = useMutation(DELETE_ORGANISATION);
    const [name, setName] = useState('');

    const disabledSave = useBoolean(true);
    const errorName = useBoolean(true);

    const close = (action?: string) => {
        onClose(action);

        if (action !== 'error') {
            errorName.setTrue();
            setName('');
        }
    };

    const onChange = (event: any) => {
        disabledSave.setFalse();
        const { value } = event.target;

        setName(value);
        const isOrgExist = organisations.find((item: any) => item.name === value && orgKey === item.key);

        errorName.setValue(Boolean(!isOrgExist));
    };

    const onDelete = () => {
        deleteOrg({
            variables: {
                key: orgKey,
            },
        })
            .then((result: any) => {
                close();
            })
            .catch((e: any) => {
                console.log('Error deleting organisation: ', e);
                close('error');
            });
    };

    return (
        <CustomDialog
            className="delete-org-dialog"
            onClose={() => close()}
            fullWidth
            scroll="paper"
            maxWidth="sm"
            open={open}
        >
            <DialogTitle id="delete-org-modal-title">
                {t('admin:deleteOrganisation')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => close()}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="add-risk-dialog-content" style={{ padding: 15 }}>
                <p className="info-text">{t('admin:areYouSureEnterName')}</p>
                <p className="info-text">{t('admin:enterOrgNameToDelete', { orgName })}</p>
                <TextField
                    className="mb-20"
                    id="org-name"
                    label={`${t('common:name')}*`}
                    value={name}
                    placeholder={`${t('admin:enterOrganisationNameToConfirm')}*`}
                    fullWidth
                    error={errorName.value}
                    variant="outlined"
                    onChange={onChange}
                />
                <DialogActions style={{ padding: 0 }}>
                    <SaveButton
                        disabled={disabledSave.value || errorName.value}
                        text={t('common:delete')}
                        onClick={onDelete}
                        id="delete-org-btn"
                    />
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};

export default DeleteOrganisation;
